import { TextFieldProps } from "@mui/material/TextField/TextField";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  FORM_ERROR_TIMEOUT_SHORT,
  REGEX_ALL_ZERO,
  REGEX_CONTAINS_LETTER_NUMBER,
} from "../../helpers/constants";
import {
  isEmpty,
  isNotEmpty,
  removeExtraCharExceptPipe,
  removeSpecialCharacters,
  removeSpecialWithPrefix,
  replaceCharPattern
} from "../../helpers/utils";
import { MuiTextField } from "../textField/MuiTextfield";
export interface CountryField {
  countryKey: string;
  countryName: string;
  postalCode: CountryFieldDetails;
  taxIDOrVATNumber: CountryFieldDetails;
}

export interface CountryFieldDetails {
  label: string;
  placeHolder: string;
  maxLength: number;
  regex: string;
  regexNotAllowed: string;
  formatErrorMessage?: string;
  blurErrorMessage?: string;
  prefix?: string;
  pattern?: string;
  isSpecialCharInFormat?: boolean;
  toolTipMessage?: string;
  isMandatory?: boolean;
  checkContainsLetterNumber?: boolean;
}

export type TextFieldWithValidationProps = TextFieldProps & {
  formId: string;
  fieldDetails: CountryFieldDetails;
  isCountryChanged: string;
  zeroValidationMessage: string;
  requiredFieldMessage: string;
  isFormReset?: boolean;
  requiredARIALabel: string;
};
export const TextFieldWithValidation = forwardRef(
  (props: TextFieldWithValidationProps, ref) => {
    const [inputValue, setInputValue] = useState<string>("");
    const [tooltipMessage, setTooltipMessage] = useState<string>("");
    const [showTooltipErrorIcon, setShowTooltipErrorIcon] =
      useState<boolean>(false);

    const { setValue, clearErrors, setError, formState } = useFormContext();

    const {
      formId,
      fieldDetails,
      isCountryChanged,
      zeroValidationMessage,
      requiredFieldMessage,
      isFormReset,
      requiredARIALabel = "Required",
      ...rest
    } = props;
    const {
      blurErrorMessage,
      regex,
      regexNotAllowed,
      formatErrorMessage,
      maxLength,
      label,
      placeHolder,
      prefix = "",
      pattern = "",
      isSpecialCharInFormat,
      toolTipMessage,
      isMandatory,
      checkContainsLetterNumber,
    } = fieldDetails;

    const regexConverted = new RegExp(regex);
    const regexNotAllowedConverted = new RegExp(regexNotAllowed);

    useEffect(() => {
      setInputValue("");
      setTooltipMessage("");
      setShowTooltipErrorIcon(false);
      setValue(formId, "");
      clearErrors(formId);
    }, [isCountryChanged, isFormReset]);

    useImperativeHandle(ref, () => ({ setDefaultValue }));

    const setDefaultValue = (defaultValue: string) => {
      setInputValue(defaultValue);
    };

    useEffect(() => {
      if (formState.submitCount) {
        checkInputMandatory(inputValue);
      }
    }, [formState.submitCount]);

    const checkInputMandatory = (input: string) => {
      if (isMandatory && isEmpty(input)) {
        setTimeout(() => {
          setError(formId, {
            type: "custom",
            message: requiredFieldMessage,
          });
        }, FORM_ERROR_TIMEOUT_SHORT);
        setTooltipMessage("");
        setShowTooltipErrorIcon(false);
      }
    };

    const checkInputBlurValidation = (input: string = "") => {
      const matchingPattern = isSinglePattern
        ? pattern
        : getMatchingPattern(input, patternArray, pattern);
      const formattedObject = replaceCharPattern(matchingPattern, input);

      let formattedNumber =
        formattedObject.formattedText + formattedObject.remainingText.join("");

      input = formattedNumber;

      const regContainsLetterNumberCheck = REGEX_CONTAINS_LETTER_NUMBER;
      regContainsLetterNumberCheck.lastIndex = 0;
      const regContainsLetterNumberTest = regContainsLetterNumberCheck.test(
        removeSpecialWithPrefix(input),
      );

      const regZeroCheck = REGEX_ALL_ZERO;
      regZeroCheck.lastIndex = 0;
      const regZeroTest = regZeroCheck.test(removeSpecialWithPrefix(input));

      if (
        (input.length > 0 && !regexConverted.test(input)) ||
        (checkContainsLetterNumber && !regContainsLetterNumberTest)
      ) {
        setTimeout(() => {
          setError(formId, {
            type: "custom",
            message: blurErrorMessage,
          });
        }, FORM_ERROR_TIMEOUT_SHORT);
        if (toolTipMessage) {
          setTooltipMessage(toolTipMessage);
          setShowTooltipErrorIcon(true);
        }
      } else if (regZeroTest) {
        setTooltipMessage("");
        setShowTooltipErrorIcon(false);
        setTimeout(() => {
          setError(formId, {
            type: "custom",
            message: zeroValidationMessage,
          });
        }, FORM_ERROR_TIMEOUT_SHORT);
      } else {
        setTooltipMessage("");
        setShowTooltipErrorIcon(false);
        clearErrors(formId);
      }

      setInputValue(input);
      setValue(formId, input);
    };

    let patternArray = pattern.split("`");
    const isSinglePattern = patternArray.length === 1;

    const getMatchingPattern = (
      input: string,
      patternArray: string[],
      defaultPattern: string,
    ): string => {
      return (
        patternArray.find((pattern) => {
          return (
            removeExtraCharExceptPipe(pattern).length ===
            removeSpecialCharacters(input).length
          );
        }) ?? defaultPattern
      );
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      let input = isNotEmpty(regexNotAllowed)
        ? removeSpecialWithPrefix(target.value)
        : target.value;
      if (!input.startsWith(prefix)) {
        input = prefix + input.substring(prefix.length);
      }

      if (input === prefix) {
        input = "";
        setInputValue(input);
        setValue(formId, input);
        checkInputMandatory(input);
        return;
      }

      checkInputBlurValidation(input);
    };

    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;

      let input = isSpecialCharInFormat
        ? removeSpecialCharacters(target.value)
        : target.value;
      if (!input.startsWith(prefix)) {
        input = prefix + input.substring(prefix.length);
      }

      // Check for not allowed characters
      if (isNotEmpty(regexNotAllowed)) {
        if (regexNotAllowedConverted.test(input)) {
          setTimeout(() => {
            setError(formId, {
              type: "custom",
              message: formatErrorMessage,
            });
            setTooltipMessage("");
            setShowTooltipErrorIcon(false);
          }, FORM_ERROR_TIMEOUT_SHORT);

          return;
        } else {
          setTooltipMessage("");
          setShowTooltipErrorIcon(false);
          clearErrors(formId);
        }

        input = input
          .toUpperCase()
          .replace(regexNotAllowedConverted, "")
          .slice(0, 100);
      }

      const matchingPattern = isSinglePattern
        ? pattern
        : getMatchingPattern(input, patternArray, pattern);

      const formattedObject = replaceCharPattern(matchingPattern, input);

      let formattedNumber =
        formattedObject.formattedText + formattedObject.remainingText.join("");

      // Update state and form value
      setInputValue(formattedNumber);
      setValue(formId, formattedNumber);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      let input = e.target.value;
      if (!input.startsWith(prefix)) {
        input = prefix + input.substring(prefix.length);
      }

      setInputValue(input);
      setValue(formId, input);
      clearErrors(formId);
    };
    const inputProps = {
      onBlur: handleBlur,
      onInput: handleInput,
      onFocus: handleFocus,
    };

    return (
      <MuiTextField
        isMandatory={isMandatory}
        label={label}
        placeholder={placeHolder}
        isErrorInfoIconEnabled={showTooltipErrorIcon}
        tooltipMessage={tooltipMessage}
        {...rest}
        {...inputProps}
        maxLength={maxLength}
        value={inputValue}
        requiredARIALabel={requiredARIALabel}
      />
    );
  },
);
